/**
 * Rover PTZ move command builders for mapping dashboard click-to-move.
 */

export const PTZ_MODEL_HANWHA = 'hanwha';
export const PTZ_MODEL_ONVIF = 'onvif';

function isHanwhaPtzModel(model) {
  return model === PTZ_MODEL_HANWHA;
}

/**
 * Pixel coords relative to the video/img element (top-left origin).
 * Inverse-rotates click position when the stream preview is CSS-rotated.
 * @param {MouseEvent} event
 * @param {HTMLElement} element
 * @param {number} [angleDeg] preview rotation in degrees (rotateZ)
 */
export function getClickCoordsInElement(event, element, angleDeg = 0) {
  const rect = element.getBoundingClientRect();
  const tileWidth = Math.round(rect.width);
  const tileHeight = Math.round(rect.height);
  let { clientX, clientY } = event;

  if (angleDeg) {
    const cx = rect.left + rect.width / 2;
    const cy = rect.top + rect.height / 2;
    const rad = (-angleDeg * Math.PI) / 180;
    const cos = Math.cos(rad);
    const sin = Math.sin(rad);
    const dx = clientX - cx;
    const dy = clientY - cy;
    clientX = cx + dx * cos - dy * sin;
    clientY = cy + dx * sin + dy * cos;
  }

  const clamp = (n, min, max) => Math.min(max, Math.max(min, n));
  return {
    x: clamp(Math.round(clientX - rect.left), 0, tileWidth),
    y: clamp(Math.round(clientY - rect.top), 0, tileHeight),
    tileWidth,
    tileHeight,
  };
}

/**
 * Hanwha areazoom point click: @,x,y,tileWidth,tileHeight
 */
export function buildHanwhaPointClickCmd({
  x,
  y,
  tileWidth,
  tileHeight,
}) {
  return `@,${x},${y},${tileWidth},${tileHeight}`;
}

/**
 * ONVIF relative click-to-move: $,3,panDelta,tiltDelta,false
 * Offset from image center scaled by step speeds.
 */
export function buildOnvifClickMoveCmd({
  x,
  y,
  tileWidth,
  tileHeight,
  panSpeed,
  tiltSpeed,
}) {
  const halfW = tileWidth / 2;
  const halfH = tileHeight / 2;
  if (halfW <= 0 || halfH <= 0) {
    return null;
  }
  const panDelta = Math.round(((x - halfW) / halfW) * panSpeed);
  const tiltDelta = Math.round(((y - halfH) / halfH) * tiltSpeed);
  if (panDelta === 0 && tiltDelta === 0) {
    return null;
  }
  return `$,3,${panDelta},${tiltDelta},false`;
}

/**
 * Point-click move command for the connected camera's PTZ model.
 * @returns {string|null}
 */
export function buildPointClickMoveCmd({
  ptzModel, x, y, tileWidth, tileHeight, panSpeed, tiltSpeed,
}) {
  if (isHanwhaPtzModel(ptzModel)) {
    return buildHanwhaPointClickCmd({
      x,
      y,
      tileWidth,
      tileHeight,
    });
  }
  return buildOnvifClickMoveCmd({
    x, y, tileWidth, tileHeight, panSpeed, tiltSpeed,
  });
}
